import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { X, FlaskConical, PackageSearch, AlertTriangle, FileText } from "lucide-react";

const GRADES = [
  {
    id: "millberry",
    name: "Millberry",
    code: "ISRI BARLEY · No.1",
    family: "Copper Wire",
    purity: "Cu ≥ 99.9%",
    description:
      "The benchmark grade of bare bright copper wire — clean, uncoated, untinned, unalloyed and free of insulation. The most liquid and highest-value scrap copper grade in international trade.",
    sources: "Stripped electrical cable, busbar offcuts, bright wire drawing scrap",
    watchouts: "Burnt or lacquered wire downgrades to No.2. Any insulation residue, tinning or oxidation is rejected at Millberry parity.",
  },
  {
    id: "berry-candy",
    name: "Berry / Candy",
    code: "ISRI BERRY / CANDY · No.1 Nodules",
    family: "Copper Nodules",
    purity: "Cu 96 – 99%",
    description:
      "Chopped or shredded copper wire nodules produced by granulation lines. Berry denotes No.1 quality from clean feedstock; Candy covers nodules with minor impurity allowances.",
    sources: "Cable granulation plants, wire chopping lines, auto wire harness processing",
    watchouts: "Free-flowing spec: max fines and dust limits apply. Plastic flake carryover and iron contamination are the common claim triggers.",
  },
  {
    id: "birch-cliff",
    name: "Birch / Cliff",
    code: "ISRI BIRCH / CLIFF · No.2",
    family: "Copper Wire",
    purity: "Cu 94 – 96%",
    description:
      "No.2 copper wire — miscellaneous unalloyed copper wire that may be tin-plated, oxidized or lightly coated. Priced at a discount to No.1 grades reflecting recovery yield.",
    sources: "Demolition wire, mixed household cable, tinned winding wire",
    watchouts: "Burnt wire, excessive insulation, hair wire and fine gauge fractions reduce payable yield. Moisture deductions common on sea freight.",
  },
  {
    id: "clove-cobra",
    name: "Clove / Cobra",
    code: "ISRI CLOVE / COBRA · Nodules",
    family: "Copper Nodules",
    purity: "Cu 94 – 99%",
    description:
      "Granulated copper wire nodules across No.1 and No.2 quality bands, traded on assay. A staple feed for secondary smelters and refineries producing cathode and ingot.",
    sources: "Granulators processing mixed cable, shredder wire recovery lines",
    watchouts: "Assay-settled: moisture, plastic and metal fines directly move the payable copper content. Always agree sampling protocol pre-shipment.",
  },
  {
    id: "cu-blister",
    name: "Copper Blister",
    code: "Smelter Grade · Cu Blister",
    family: "Primary/Secondary Copper",
    purity: "Cu 98.5 – 99.5%",
    description:
      "Partially refined copper cast in slab form — the intermediate product of primary smelting, traded to refineries for electrolytic refining into LME-grade cathode.",
    sources: "Primary and secondary smelters, converting operations",
    watchouts: "Payable metals clause matters: Au/Ag credits, and penalties on As, Sb, Bi and Pb. Settlement on umpire assay is standard.",
  },
  {
    id: "al-6063",
    name: "6063 Extrusion",
    code: "ISRI TABOO / TAINT TABOR",
    family: "Aluminum",
    purity: "Al ~98.9% · Mg 0.45–0.9% · Si 0.2–0.6%",
    description:
      "Clean architectural extrusion alloy — the workhorse of window frames and profiles. Taboo covers new production scrap; Taint Tabor covers clean old/painted extrusion free of thermal break and iron.",
    sources: "Window & façade fabricators, demolition of commercial glazing systems",
    watchouts: "Thermal-break (polyamide) inserts, steel screws and 6061 contamination downgrade the load. Painted vs bare pricing differs.",
  },
  {
    id: "al-ingot",
    name: "Al Scrap Ingot",
    code: "Secondary Remelt Ingot",
    family: "Aluminum",
    purity: "Per spec · typically LM-series",
    description:
      "Secondary aluminum remelted and cast into ingot or sow form for foundry feedstock. Traded against LM designations and chemical composition certificates.",
    sources: "Secondary smelters, dross recovery and remelt operations",
    watchouts: "Full chemistry certificate per cast is essential. Fe and Zn pickup from dirty feedstock is the main quality risk.",
  },
  {
    id: "zorba",
    name: "Zorba",
    code: "ISRI ZORBA · Shredded NF",
    family: "Mixed Non-Ferrous",
    purity: "NF ≥ 90% typical · predominantly Al",
    description:
      "Shredded mixed non-ferrous metals from auto shredder residue — predominantly aluminum with zinc, copper, brass and stainless fractions. The feedstock for heavy-media and sensor sorting plants.",
    sources: "Auto shredders, ELV dismantling, white-goods fragmentation",
    watchouts: "Traded on NF content and aluminum yield assays. Fines percentage, moisture and magnesium contamination drive claims.",
  },
  {
    id: "talon",
    name: "Talon",
    code: "ISRI TALON · Shredded Al",
    family: "Aluminum",
    purity: "Al ≥ 90% typical",
    description:
      "Shredded aluminum scrap recovered from shredder non-ferrous streams — eddy-current separated and upgraded fragments, sized for direct furnace charging.",
    sources: "Auto shredder NF plants, Zorba upgrading lines, white-goods fragmentation",
    watchouts: "Zinc and magnesium pickup compresses value. Fines, oil and moisture are the standard deduction points on delivery.",
  },
  {
    id: "tense",
    name: "Tense",
    code: "ISRI TENSE · Cast Al",
    family: "Aluminum",
    purity: "Al ≥ 94% typical",
    description:
      "Clean old aluminum castings — engine blocks, gearbox housings and structural castings, free of attached iron, steel inserts and excessive oil.",
    sources: "ELV dismantling, engine recycling lines, foundry returns",
    watchouts: "Iron attachments (liners, studs) downgrade the load. Oil-saturated castings face rejection or heavy deduction at smelter gate.",
  },
  {
    id: "taint-tabor",
    name: "Taint Tabor",
    code: "ISRI TAINT / TABOR · Old Sheet",
    family: "Aluminum",
    purity: "Al ~95% recovery basis",
    description:
      "Old sheet aluminum — painted siding, gutters, downpipes and mixed clean sheet. The workhorse grade of the architectural demolition stream, traded on recovery yield.",
    sources: "Building demolition, cladding replacement, fabricator skeleton offcuts",
    watchouts: "Thermal-break and plastic attachments, steel fasteners, and mixed-alloy contamination drive claims. Painted vs bare pricing differs.",
  },
  {
    id: "ubc-cans",
    name: "UBC Cans",
    code: "ISRI TALDON · Baled UBC",
    family: "Aluminum",
    purity: "Al ≥ 97% can body",
    description:
      "Used beverage cans, baled or densified — one of the most recycled products on earth and the core feedstock for can-sheet remelt loops.",
    sources: "MRF recovery lines, deposit-return schemes, beverage distribution take-back",
    watchouts: "Moisture, sand and food contamination are priced in heavily. Expect 15–20% melt loss on lacquer and coating burn-off — settlement is on net recovered metal.",
  },
  {
    id: "brass-honey",
    name: "Brass Honey",
    code: "ISRI HONEY · Yellow Brass",
    family: "Brass",
    purity: "Cu ~60 – 65% · Zn balance",
    description:
      "Mixed yellow brass solids — castings, rolled brass, rod and tubing, including plated material. A staple of plumbing and machining scrap trade.",
    sources: "Plumbing & valve scrap, machining turnings (sorted), demolition fixtures",
    watchouts: "Iron contamination and radiator-end admixture downgrade. Lead content in free-cutting brasses matters for potable-water compliant remelts.",
  },
  {
    id: "twitch",
    name: "Twitch",
    code: "ISRI TWITCH · Floated Shred Al",
    family: "Aluminum",
    purity: "Al 92 – 95% typical",
    description:
      "Floated shredded aluminum from fragmentizer plants — heavy-media separated fragments with the zinc and lead sinks removed, ready for direct remelt.",
    sources: "Auto shredder sink-float plants, Zorba heavy-media upgrading",
    watchouts: "Residual zinc and magnesium pickup caps the achievable alloy. Rubber and plastic floats, plus moisture, are standard deduction points.",
  },
  {
    id: "tweak",
    name: "Tweak",
    code: "ISRI TWEAK · Burnt Shred Al",
    family: "Aluminum",
    purity: "Al ~90% typical",
    description:
      "Burnt fragmentizer aluminum — incinerated shredded scrap with oxidized surfaces and coatings removed by thermal processing.",
    sources: "Incinerated auto shred, thermally processed cable recovery",
    watchouts: "Oxidation drives higher melt loss and dross generation than Twitch. Ash content and residual fines are priced against recovery.",
  },
  {
    id: "zebra",
    name: "Zebra",
    code: "ISRI ZEBRA · Heavy NF Shred",
    family: "Mixed Non-Ferrous",
    purity: "Zn / Cu / stainless heavies",
    description:
      "High-density non-ferrous concentrate from the heavy-media sink fraction — zinc die-cast, copper, brass and stainless fragments.",
    sources: "Sink-float plants, dense-media separation lines",
    watchouts: "Assay-settled material: printed circuit boards and wire-harness carryover are penalized. Agree sampling protocol before shipment.",
  },
  {
    id: "ebony",
    name: "Ebony",
    code: "ISRI EBONY · Red Brass",
    family: "Brass/Bronze",
    purity: "Cu ~80 – 85%",
    description:
      "Red brass and composition solids — valves, fittings and plumbing bronze with higher copper content than yellow brass, commanding a premium.",
    sources: "Plumbing demolition, foundry gates and risers, marine fittings",
    watchouts: "Iron attachments and aluminum-bronze admixture downgrade. Lead content varies — matters for potable-water compliant remelts.",
  },
  {
    id: "ocean",
    name: "Ocean",
    code: "ISRI OCEAN · Brass Radiators",
    family: "Radiators",
    purity: "Cu 30 – 35% typical",
    description:
      "Unsweated brass and copper auto radiators — complete cores traded on recoverable copper and brass content after solder separation.",
    sources: "Auto dismantlers, radiator repair shops, ELV yards",
    watchouts: "Solder (Pb/Sn) loading reduces payable copper. Plastic tanks and iron side-frames are deducted — swept vs unsweated pricing differs materially.",
  },
  {
    id: "talk",
    name: "Talk",
    code: "ISRI TALK · Al-Cu Radiators",
    family: "Radiators",
    purity: "Al ~50% · Cu 25 – 30%",
    description:
      "Aluminum radiators with copper tubing (ACRs) from air-conditioning and cooling systems — valued on the separated copper yield.",
    sources: "HVAC demolition, AC unit recycling, auto cooling systems",
    watchouts: "Iron frames, plastic end tanks and oil residue drive deductions. Separation efficiency is the whole trade — agree yield basis upfront.",
  },
  {
    id: "parch",
    name: "Parch",
    code: "ISRI PARCH · Copper Radiators",
    family: "Radiators",
    purity: "Cu 45 – 55% typical",
    description:
      "Clean copper radiators, unsweated — the highest-copper radiator grade, traded close to No.2 copper parity on recovery.",
    sources: "Industrial cooling systems, vintage auto and plant radiators",
    watchouts: "Paint, solder and residual coolant are deducted. Confirm unsweated status — solder-swept cores carry a meaningful discount.",
  },
];

export default function GradeSpecs() {
  const [active, setActive] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="mt-16 lg:mt-24" data-testid="grade-spec-library">
      <Reveal>
        <div className="flex items-center gap-4 mb-4">
          <span className="font-mono-tech text-xs text-[#64748B]">/02-A</span>
          <span className="h-px w-12 bg-[#E06D28]/60" />
          <span className="overline-label">Grade Spec Library</span>
        </div>
        <p className="text-sm text-[#94A3B8] max-w-xl mb-8">
          Tap any grade for its reference specification. Indicative only — final terms are always set by contract and assay.
        </p>
      </Reveal>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {GRADES.map((g, i) => (
          <Reveal key={g.id} delay={i * 0.04}>
            <button
              data-testid={`grade-button-${g.id}`}
              onClick={() => setActive(g)}
              className="w-full text-left card-industrial p-5 group cursor-pointer"
            >
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#64748B] group-hover:text-[#F07D38] transition-colors duration-300">
                {g.family}
              </div>
              <div className="font-display font-bold text-base sm:text-lg text-[#F4F6F8] mt-1.5 group-hover:text-[#F07D38] transition-colors duration-300">
                {g.name}
              </div>
              <div className="font-mono-tech text-[11px] text-[#64748B] mt-1">{g.purity}</div>
            </button>
          </Reveal>
        ))}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="grade-spec-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8 bg-[#0A0D12]/85 backdrop-blur-md"
            onClick={() => setActive(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 48, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 32, scale: 0.97 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-[#111620] border border-[#E06D28]/30 rounded-md p-7 sm:p-10"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="absolute top-0 left-0 w-10 h-10 border-t-2 border-l-2 border-[#E06D28]" />
              <span className="absolute bottom-0 right-0 w-10 h-10 border-b-2 border-r-2 border-[#E06D28]" />

              <button
                data-testid="grade-spec-close"
                onClick={() => setActive(null)}
                aria-label="Close spec sheet"
                className="absolute top-5 right-5 text-[#64748B] hover:text-[#F07D38] transition-colors"
              >
                <X size={22} />
              </button>

              <div className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-[#F07D38]">{active.code}</div>
              <h3 className="font-display font-black text-3xl sm:text-4xl text-[#F4F6F8] mt-2">{active.name}</h3>
              <div className="inline-block font-mono-tech text-xs font-semibold text-[#0A0D12] bg-[#F3B700] px-3 py-1.5 rounded-sm mt-4">
                {active.purity}
              </div>

              <div className="mt-8 space-y-6">
                <div className="flex gap-4">
                  <FileText size={18} className="text-[#E06D28] shrink-0 mt-0.5" />
                  <div>
                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#64748B] mb-1.5">Specification</div>
                    <p className="text-sm text-[#94A3B8] leading-relaxed">{active.description}</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <PackageSearch size={18} className="text-[#E06D28] shrink-0 mt-0.5" />
                  <div>
                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#64748B] mb-1.5">Typical Sources</div>
                    <p className="text-sm text-[#94A3B8] leading-relaxed">{active.sources}</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <AlertTriangle size={18} className="text-[#F3B700] shrink-0 mt-0.5" />
                  <div>
                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#64748B] mb-1.5">Quality Watch-outs</div>
                    <p className="text-sm text-[#94A3B8] leading-relaxed">{active.watchouts}</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-[#283244] flex items-center gap-3 text-[11px] text-[#64748B] font-mono-tech">
                <FlaskConical size={13} className="text-[#E06D28]" />
                Indicative reference — final specifications agreed per contract & independent assay
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
